import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, Boxes, Building2, ChevronDown, Flame, Layers, PenTool, Wind, Wrench, Zap,
} from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const VIEWPORT = { once: true, margin: "-80px" };

const HERO_IMAGE = "https://images.pexels.com/photos/19895882/pexels-photo-19895882.jpeg?auto=compress&cs=tinysrgb&w=1800";
const PANEL_IMAGES = {
  civil: "https://images.pexels.com/photos/3862379/pexels-photo-3862379.jpeg?auto=compress&cs=tinysrgb&w=1600",
  cad: HERO_IMAGE,
  electrical: "https://images.pexels.com/photos/3912948/pexels-photo-3912948.jpeg?auto=compress&cs=tinysrgb&w=1600",
  fire: "/service-fire.jpg",
  piping: "/service-piping.jpg",
  cleanroom: "https://images.pexels.com/photos/9574399/pexels-photo-9574399.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

const DESIGN_SERVICES = [
  {
    icon: Building2,
    short: "Civil & Structural",
    title: "CIVIL & STRUCTURAL DESIGN & DRAFTING",
    desc: "Civil and structural design and drafting for industrial, pharmaceutical, laboratory and commercial facilities, providing coordinated layouts, detailed drawings and technical documentation for project execution.",
    scope: ["Civil design & layouts", "Structural design & detailing", "Construction drawings", "CAD drafting & documentation"],
    details: [
      "Civil & Structural Design",
      "General Arrangement & Layout Drawings",
      "Structural Detailing & Reinforcement Drawings",
      "Construction & Working Drawings",
      "2D CAD Drafting & Drawing Development",
      "As-Built & Technical Documentation",
    ],
    image: PANEL_IMAGES.civil,
    badge: "CAD • STRUCTURAL • DETAILS",
  },
  {
    icon: Layers,
    short: "Interior & Laboratory",
    title: "INTERIOR & LABORATORY FIT-OUT DESIGN",
    desc: "Design and drafting solutions for industrial, pharmaceutical and laboratory environments, providing coordinated layouts, detailed drawings and execution-ready documentation.",
    scope: ["Interior fit-out design", "Laboratory layouts", "Cleanroom design", "Detailed CAD drawings"],
    details: [
      "Interior Fit-Out Layouts",
      "Laboratory Layout & Detailing",
      "Cleanroom Layout Drawings",
      "Partition, Ceiling & Flooring Drawings",
      "Interior Elevations, Sections & Details",
      "CAD Drafting & Construction Documentation",
    ],
    image: "/design-interior-lab.jpg",
    badge: "INTERIORS • LAB • FIT-OUTS",
    kenburns: true,
  },
  {
    icon: Wind,
    short: "HVAC",
    title: "HVAC DESIGN & DRAFTING",
    desc: "HVAC design and drafting solutions developed for industrial, pharmaceutical, laboratory and commercial facilities, with coordinated layouts and technical documentation for execution.",
    scope: ["HVAC design & engineering", "Equipment & system selection", "Ducting & air distribution", "Cleanroom HVAC design"],
    details: [
      "HVAC Load Calculations & Design",
      "AHU / FCU System Design",
      "Chilled Water & VRF / VRV Design",
      "Ducting & Air Distribution Drawings",
      "Cleanroom HVAC & HEPA Systems",
      "HVAC Layouts, Schematics & Documentation",
    ],
    image: "/design-hvac.jpg",
    badge: "HVAC • BIM • COORDINATION",
    kenburns: true,
  },
  {
    icon: Zap,
    short: "Electrical",
    title: "ELECTRICAL DESIGN & DRAFTING",
    desc: "Electrical engineering design and drafting for industrial, pharmaceutical, laboratory and commercial facilities, covering power distribution, lighting, protection and coordinated electrical documentation.",
    scope: ["Power distribution design", "Lighting & small power", "Electrical protection", "CAD drawings & documentation"],
    details: [
      "Electrical Load Assessment & SLDs",
      "LT Power Distribution Design",
      "Cable & Feeder Sizing",
      "Lighting & Small Power Layouts",
      "Earthing & Lightning Protection",
      "Electrical Layouts, Schedules & Documentation",
    ],
    image: "/design-electrical.jpg",
    badge: "POWER • CAD • DOCUMENTATION",
    kenburns: true,
  },
  {
    icon: Flame,
    short: "Fire Protection",
    title: "FIRE PROTECTION DESIGN",
    desc: "Fire protection design and drafting for industrial, pharmaceutical, laboratory and commercial facilities, providing coordinated fire safety layouts and technical documentation.",
    scope: ["Fire water system design", "Hydrant & sprinkler design", "Fire detection & alarm", "Fire protection drawings"],
    details: [
      "Fire Protection System Design",
      "Fire Hydrant & Fire Water Layouts",
      "Sprinkler System Design",
      "Fire Pump & Pump Room Layouts",
      "Detection & Alarm Layouts",
      "Fire Protection Drawings & Documentation",
    ],
    image: "/design-fire.jpg",
    badge: "FIRE • LAYOUTS • COMPLIANCE",
    kenburns: true,
  },
  {
    icon: Wrench,
    short: "Process & Utility Piping",
    title: "PROCESS & UTILITY PIPING DESIGN",
    desc: "Process and utility piping design and drafting for industrial and pharmaceutical facilities, covering coordinated piping layouts, routing, details and technical documentation for execution.",
    scope: ["Process piping design", "Utility piping design", "Piping layout & routing", "Isometrics & detail drawings"],
    details: [
      "Process & Utility Piping Design",
      "Piping Layout & Routing",
      "Equipment & Piping Arrangement",
      "Isometric & Detailed Drawings",
      "Pipe Supports & Related Details",
      "Piping Documentation & As-Built Drawings",
    ],
    image: "/design-piping.jpg",
    badge: "PIPING • 3D MODEL • ISOMETRICS",
    kenburns: true,
    pan: true,
  },
  {
    icon: Boxes,
    short: "Cleanroom",
    title: "CLEANROOM & CONTROLLED ENVIRONMENT DESIGN",
    desc: "Design and drafting for cleanrooms and controlled environments, integrating room layouts, pressure relationships, airflow requirements and coordinated facility documentation.",
    scope: ["Cleanroom layouts", "Pressure cascade", "Airflow & HVAC coordination", "Detail drawings & documentation"],
    details: [
      "Cleanroom Layout & Zoning",
      "Pressure Cascade Planning",
      "Airflow & HVAC Coordination",
      "Room Finish & Partition Details",
      "Cleanroom Equipment Layouts",
      "Construction & Technical Drawings",
    ],
    image: "/design-cleanroom.jpg",
    badge: "CLEANROOM • HVAC • CONTROLS",
    kenburns: true,
  },
  {
    icon: PenTool,
    short: "CAD / BIM",
    title: "2D / 3D CAD & BIM DOCUMENTATION",
    desc: "Professional CAD and BIM documentation services supporting engineering design, coordination, drawing development and construction-ready project documentation.",
    scope: ["2D CAD drafting", "3D modelling", "BIM coordination", "Technical documentation"],
    details: [
      "2D CAD Drafting",
      "3D Engineering Modelling",
      "BIM Model Development",
      "Design Coordination Drawings",
      "Drawing Revision & Documentation",
      "As-Built & Record Drawings",
    ],
    image: "/design-cadbim.jpg",
    badge: "2D CAD • 3D BIM • CLASH CHECKS",
    kenburns: true,
  },
];

const APPROACH_STAGES = [
  { num: "01", title: "CONCEPT", desc: "Understand requirements and establish the design basis." },
  { num: "02", title: "ENGINEER", desc: "Develop calculations, systems and technical solutions." },
  { num: "03", title: "COORDINATE", desc: "Integrate disciplines and resolve design interfaces." },
  { num: "04", title: "DELIVER", desc: "Issue coordinated drawings and execution-ready documentation." },
];

const useIsCompact = () => {
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 1023px)");
    const update = () => setCompact(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return compact;
};

const CadPattern = () => (
  <svg
    aria-hidden="true"
    data-testid="design-cad-pattern"
    className="w-full h-full text-[#8FB2D9]"
    viewBox="0 0 1200 900"
    preserveAspectRatio="none"
  >
    <defs>
      <pattern id="design-cad-grid" width="72" height="72" patternUnits="userSpaceOnUse">
        <path d="M 72 0 L 0 0 0 72" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="900" fill="url(#design-cad-grid)" />
    <g fill="none" stroke="currentColor" strokeWidth="1.2">
      <path d="M100 720 H350 V570 H530 V720 H790 V510 H1080" />
      <path d="M160 220 H410 V340 H660 V220 H980" />
      <path d="M780 720 L980 510 M530 720 L790 510" />
      <path d="M120 120 H360 M120 105 V135 M360 105 V135" />
      <path d="M860 780 H1090 M860 765 V795 M1090 765 V795" />
      <circle cx="410" cy="340" r="38" />
      <circle cx="980" cy="220" r="48" />
      <path d="M932 220 H1028 M980 172 V268" />
    </g>
  </svg>
);

const DarkCadPattern = () => (
  <svg
    aria-hidden="true"
    data-testid="design-selector-pattern"
    className="w-full h-full text-[#9AB3CC]"
    viewBox="0 0 1200 800"
    preserveAspectRatio="none"
  >
    <defs>
      <pattern id="design-selector-grid" width="64" height="64" patternUnits="userSpaceOnUse">
        <path d="M 64 0 L 0 0 0 64" fill="none" stroke="currentColor" strokeWidth="1" />
      </pattern>
    </defs>
    <rect width="1200" height="800" fill="url(#design-selector-grid)" />
    <g fill="none" stroke="currentColor" strokeWidth="1.1">
      <path d="M80 640 H340 V500 H540 V640 H820 V430 H1100" />
      <path d="M150 180 H430 V310 H690 V180 H1010" />
      <path d="M130 90 H410 M130 76 V104 M410 76 V104" />
      <path d="M860 720 H1120 M860 706 V734 M1120 706 V734" />
      <circle cx="430" cy="310" r="34" />
      <circle cx="1010" cy="180" r="46" />
      <path d="M964 180 H1056 M1010 134 V226" />
      <path d="M760 640 L1010 430 M540 640 L820 430" />
    </g>
  </svg>
);

const AccentLine = ({ delay, duration = 0.45, className = "" }) => (
  <motion.span initial="hidden" whileInView="show" viewport={VIEWPORT} className={`block ${className}`}>
    <motion.span
      variants={{
        hidden: { scaleX: 0 },
        show: { scaleX: 1, transition: { duration, delay, ease: EASE } },
      }}
      className="block h-full w-full origin-left bg-[#F47721]"
    />
  </motion.span>
);

const MaskedLine = ({ children, delay, duration = 0.72, className = "" }) => (
  <motion.span initial="hidden" whileInView="show" viewport={VIEWPORT} className={`block overflow-hidden ${className}`}>
    <motion.span
      variants={{
        hidden: { x: "-102%" },
        show: { x: 0, transition: { duration, delay, ease: EASE } },
      }}
      className="block"
    >
      {children}
    </motion.span>
  </motion.span>
);

const DrawingCadLines = () => (
  <motion.svg
    aria-hidden="true"
    data-testid="design-background-drawing"
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    className="w-full h-full text-[#8FB2D9]"
    viewBox="0 0 1200 900"
    preserveAspectRatio="none"
  >
    {[
      "M80 180 H410 V330 H700 V180 H1080",
      "M120 760 H390 V600 H620 V760 H960 V540 H1120",
      "M180 120 H520 M180 105 V135 M520 105 V135",
      "M820 810 H1110 M820 795 V825 M1110 795 V825",
    ].map((d, i) => (
      <motion.path
        key={d}
        d={d}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          show: {
            pathLength: 1,
            opacity: 1,
            transition: { duration: 1.2, delay: 1.0 + i * 0.16, ease: EASE },
          },
        }}
      />
    ))}
  </motion.svg>
);

const HeroDimensionLines = () => (
  <motion.div
    aria-hidden="true"
    data-testid="design-hero-dimensions"
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    variants={{
      hidden: { opacity: 0 },
      show: { opacity: [0, 0.16, 0.05], transition: { duration: 2.8, delay: 1.35, times: [0, 0.42, 1], ease: "easeOut" } },
    }}
    className="absolute -left-10 top-14 hidden md:block w-[680px] h-[280px] text-[#8FB2D9] pointer-events-none"
  >
    <motion.svg viewBox="0 0 680 280" className="w-full h-full" fill="none">
      {[
        "M18 74 H310 M18 60 V88 M310 60 V88",
        "M352 38 H642 M352 24 V52 M642 24 V52",
        "M70 238 H610 M70 224 V252 M610 224 V252",
        "M570 74 V210 M556 74 H584 M556 210 H584",
        "M28 196 C120 150 220 150 330 196",
      ].map((d, i) => (
        <motion.path
          key={d}
          d={d}
          stroke="currentColor"
          strokeWidth="1.2"
          variants={{
            hidden: { pathLength: 0 },
            show: { pathLength: 1, transition: { duration: 0.85, delay: 1.45 + i * 0.12, ease: EASE } },
          }}
        />
      ))}
      <motion.circle
        cx="610"
        cy="238"
        r="22"
        stroke="currentColor"
        strokeWidth="1.2"
        variants={{
          hidden: { pathLength: 0 },
          show: { pathLength: 1, transition: { duration: 0.7, delay: 2.1, ease: EASE } },
        }}
      />
    </motion.svg>
  </motion.div>
);
const MaskedWords = ({ words, delay, stagger = 0.12, duration = 0.48, className = "" }) => (
  <motion.span initial="hidden" whileInView="show" viewport={VIEWPORT} className={`block ${className}`}>
    {words.map((word, i) => (
      <span key={word} className={`inline-block overflow-hidden align-top pb-[0.08em] -mb-[0.08em] ${i < words.length - 1 ? "mr-[0.22em]" : ""}`}>
        <motion.span
          variants={{
            hidden: { opacity: 0, y: 20 },
            show: { opacity: 1, y: 0, transition: { duration, delay: delay + i * stagger, ease: EASE } },
          }}
          className="inline-block"
        >
          {word}
        </motion.span>
      </span>
    ))}
  </motion.span>
);

const SecondTitleAccent = ({ delay }) => (
  <motion.span
    aria-hidden="true"
    initial="hidden"
    whileInView="show"
    viewport={VIEWPORT}
    className="absolute -left-4 -right-4 top-[58%] h-px pointer-events-none"
  >
    <motion.span
      data-testid="design-second-title-accent"
      className="block h-full w-full origin-left bg-[#F47721]"
      variants={{
        hidden: { scaleX: 0, opacity: 0 },
        show: {
          scaleX: 1,
          opacity: [0, 0.32, 0.12],
          transition: {
            scaleX: { duration: 0.75, delay, ease: EASE },
            opacity: { duration: 1.4, delay, times: [0, 0.35, 1], ease: "easeOut" },
          },
        },
      }}
    />
  </motion.span>
);


export const DesignServices = () => {
  const [active, setActive] = useState(0);
  const [scopeOpen, setScopeOpen] = useState(false);

  const compact = useIsCompact();
  const timing = compact
    ? {
        labelLine: 0.7,
        labelText: 0.95,
        titleOne: 1.2,
        titleTwo: 2.0,
        titleTwoAccent: 1.95,
        underline: 2.62,
        description: 2.95,
        reveal: 3.5,
      }
    : {
        labelLine: 1.0,
        labelText: 1.35,
        titleOne: 1.5,
        titleTwo: 2.8,
        titleTwoAccent: 2.65,
        underline: 3.55,
        description: 4.0,
        reveal: 4.65,
      };
  const revealBase = timing.reveal;
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start end", "end start"] });
  const cadY = useTransform(scrollYProgress, [0, 1], [34, -34]);
  const current = DESIGN_SERVICES[active];
  const ActiveIcon = current.icon;

  const go = (e, target = "#contact") => {
    e.preventDefault();
    window.__lenis?.scrollTo(target, { offset: -76, duration: 1.4 });
  };

  const tabsContainerVariants = {
    hidden: { opacity: 0, y: 22 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: revealBase + 0.05,
        ease: EASE,
        delayChildren: revealBase + 0.13,
        staggerChildren: 0.1,
      },
    },
  };

  const tabItemVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { duration: 0.32, ease: EASE } },
  };

  return (
    <section id="design" ref={sectionRef} data-testid="design-section" className="relative overflow-hidden bg-[#F7F7F4] py-24 lg:py-32">
      <div className="absolute inset-x-0 top-0 h-[760px] lg:h-[820px] overflow-hidden bg-gradient-to-br from-[#0E2742] to-[#173F68]" data-testid="design-hero-background">
        <motion.img
          src={HERO_IMAGE}
          alt="Engineer developing coordinated technical drawings and a 3D project model"
          className="absolute inset-0 w-full h-full object-cover grayscale-[45%]"
          initial={{ opacity: 0, scale: 1, x: 0 }}
          whileInView={{ opacity: 0.18, scale: 1.02, x: -10 }}
          viewport={VIEWPORT}
          transition={{
            opacity: { duration: 1.0, ease: EASE },
            scale: { duration: 11, ease: "easeInOut" },
            x: { duration: 11, ease: "easeInOut" },
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0E2742]/88 via-[#102F4F]/72 to-[#173F68]/82" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1726]/82 via-[#0B1726]/42 to-transparent" />
        <motion.div aria-hidden="true" style={{ y: cadY }} className="absolute inset-0 opacity-[0.07] pointer-events-none">
          <CadPattern />
        </motion.div>
        <div aria-hidden="true" className="absolute inset-0 opacity-[0.08] pointer-events-none">
          <DrawingCadLines />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#F7F7F4]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
        <div className="max-w-5xl relative" data-testid="design-hero">
          <HeroDimensionLines />
          <div className="relative z-10 flex items-center gap-4">
            <AccentLine delay={timing.labelLine} duration={0.35} className="h-[2px] w-10" />
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={VIEWPORT}
              transition={{ duration: 0.4, delay: timing.labelText, ease: EASE }}
              className="font-inter text-[12px] font-semibold uppercase tracking-[0.32em] text-[#F47721]"
              data-testid="design-label"
            >
              Design & Engineering
            </motion.p>
          </div>

          <h2 className="relative z-10 font-manrope font-bold tracking-tight leading-[1.06] text-[38px] sm:text-[44px] md:text-[52px] lg:text-[60px] xl:text-[64px] mt-7" data-testid="design-heading">
            <MaskedWords
              words={["ENGINEERED", "WITH", "PRECISION."]}
              delay={timing.titleOne}
              stagger={compact ? 0.1 : 0.12}
              duration={compact ? 0.42 : 0.52}
              className="text-white"
            />
            <span className="relative inline-block">
              <SecondTitleAccent delay={timing.titleTwoAccent} />
              <MaskedLine delay={timing.titleTwo} duration={compact ? 0.55 : 0.7} className="relative z-10 text-[#F47721]">DETAILED FOR EXECUTION.</MaskedLine>
              <AccentLine delay={timing.underline} duration={0.4} className="absolute -bottom-2 left-0 z-10 h-[2px] w-[10ch] md:w-full" />
            </span>
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: timing.description, ease: EASE }}
            className="relative z-10 font-inter font-medium text-[#C8D2DC] text-[15px] md:text-base lg:text-[17px] leading-[1.62] mt-8 max-w-[680px]"
            data-testid="design-description"
          >
            We deliver engineering design, drafting and coordinated technical drawings for industrial, pharmaceutical, laboratory and commercial facilities — developed for accuracy, coordination and execution.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: timing.description + 0.45, ease: EASE }}
            className="relative z-10 mt-10"
            data-testid="design-signature-caption"
          >
            <p className="font-manrope text-[22px] md:text-[26px] lg:text-[28px] font-medium uppercase tracking-[0.08em] text-[#E8EDF2] leading-[1.2]">
              Where Vision Meets
            </p>
            <span className="relative mt-1 inline-block">
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEWPORT}
                transition={{ duration: 0.55, delay: timing.description + 0.62, ease: EASE }}
                className="block font-manrope text-[22px] md:text-[26px] lg:text-[28px] font-medium uppercase tracking-[0.08em] text-[#F47721] leading-[1.2]"
              >
                Engineering Excellence.
              </motion.span>
              <AccentLine delay={timing.description + 1.05} duration={0.4} className="absolute -bottom-1.5 left-0 h-[2px] w-full" />
            </span>
          </motion.div>
        </div>

        <div className="relative mt-20 lg:mt-24 overflow-hidden rounded-md border border-[rgba(255,255,255,0.12)] bg-gradient-to-br from-[#101C2B] to-[#172A3A] p-6 md:p-8 lg:p-10" data-testid="design-selector">
          <motion.div
            aria-hidden="true"
            className="absolute inset-[-8%] opacity-[0.055] pointer-events-none"
            animate={{ x: [-24, 24, -24], y: [-10, 10, -10] }}
            transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          >
            <DarkCadPattern />
          </motion.div>
          <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-br from-[#101C2B]/45 via-transparent to-[#172A3A]/55 pointer-events-none" />

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.5, delay: revealBase, ease: EASE }}
            className="relative z-10 font-inter text-[12px] font-semibold uppercase tracking-[0.3em] text-[#F47721]"
            data-testid="design-capabilities-label"
          >
            Design Capabilities
          </motion.p>
          <motion.h3
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: revealBase + 0.08, ease: EASE }}
            className="relative z-10 font-manrope font-bold tracking-tight text-white text-[34px] md:text-[42px] lg:text-[46px] leading-[1.1] mt-5 max-w-4xl"
            data-testid="design-capabilities-heading"
          >
            ENGINEERED FOR EVERY DETAIL.
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: revealBase + 0.16, ease: EASE }}
            className="relative z-10 font-inter text-[#C8D2DC] text-base md:text-[17px] leading-[1.65] mt-5 max-w-2xl"
            data-testid="design-capabilities-description"
          >
            From engineering design and drafting to coordinated technical documentation, our multidisciplinary capabilities are developed for accuracy, coordination and execution.
          </motion.p>

          <div className="relative z-10 mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={VIEWPORT}
              variants={tabsContainerVariants}
              className="lg:col-span-5 self-start relative overflow-hidden"
              data-testid="design-tabs"
            >
              <motion.span
                aria-hidden="true"
                data-testid="design-capability-scan-line"
                className="absolute top-0 bottom-0 left-0 z-20 w-[2px] bg-[#F47721] pointer-events-none"
                variants={{
                  hidden: { left: "0%", opacity: 0 },
                  show: {
                    left: "100%",
                    opacity: [0, 1, 1, 0],
                    transition: {
                      left: { duration: 1.05, delay: revealBase + 0.18, ease: "linear" },
                      opacity: { duration: 1.05, delay: revealBase + 0.18, times: [0, 0.08, 0.88, 1], ease: "linear" },
                    },
                  },
                }}
              />
              <div role="tablist" aria-label="Design capabilities">
                {DESIGN_SERVICES.map((service, i) => {
                  const Icon = service.icon;
                  const selected = active === i;
                  return (
                    <motion.button
                      key={service.title}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      data-testid={`design-tab-${i}`}
                      onClick={() => {
                        setActive(i);
                        setScopeOpen(false);
                      }}
                      variants={tabItemVariants}
                      whileHover={{ y: -2 }}
                      className={`group relative w-full flex items-center gap-4 px-5 py-[18px] mb-3 last:mb-0 text-left rounded-sm border backdrop-blur-sm transition-[background-color,border-color,box-shadow] duration-300 outline-none focus-visible:ring-2 focus-visible:ring-[#F47721] focus-visible:ring-inset ${
                        selected ? "border-[#F47721]/30 bg-[#F47721]/10 shadow-[0_0_24px_rgba(244,119,33,0.10)]" : "border-[rgba(255,255,255,0.12)] bg-white/[0.04] hover:bg-white/[0.07]"
                      }`}
                    >
                      <span aria-hidden="true" className={`absolute left-0 top-0 bottom-0 w-[4px] bg-[#F47721] transition-opacity duration-300 ${selected ? "opacity-100" : "opacity-0"}`} />
                      <span className={`font-manrope text-[17px] font-bold tracking-[0.08em] transition-colors duration-300 ${selected ? "text-[#F47721]" : "text-[#8E9AAA] group-hover:text-[#F47721]"}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className={`w-12 h-12 shrink-0 rounded-sm border flex items-center justify-center transition-[transform,border-color,background-color,color,box-shadow] duration-300 group-hover:-translate-y-0.5 group-hover:border-[#F47721]/60 group-hover:text-[#F47721] group-hover:shadow-[0_0_18px_rgba(244,119,33,0.12)] ${
                        selected ? "border-[#F47721]/45 text-[#F47721] bg-[#F47721]/10" : "border-[rgba(255,255,255,0.15)] text-[#C8D2DC] bg-white/[0.04]"
                      }`}>
                        <Icon size={21} strokeWidth={1.6} />
                      </span>
                      <span className={`font-manrope font-semibold text-[18px] md:text-[19px] tracking-[0.02em] leading-snug transition-colors duration-300 ${selected ? "text-white" : "text-[#E8EDF2]"}`}>
                        {service.short}
                      </span>
                      <ArrowUpRight
                        size={18}
                        className={`ml-auto shrink-0 transition-[opacity,transform,color] duration-300 ${selected ? "opacity-100 translate-x-0 text-[#F47721]" : "opacity-0 translate-x-1 text-[#8E9AAA] group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-[#F47721]"}`}
                      />
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={VIEWPORT}
              variants={{
                hidden: { opacity: 0, y: 22 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5, delay: revealBase + 0.2, ease: EASE } },
              }}
              className="lg:col-span-7 bg-white/[0.04] border border-[rgba(255,255,255,0.12)] rounded-md overflow-hidden min-h-[560px] flex flex-col backdrop-blur-sm"
              data-testid="design-panel"
            >
              <motion.div className="flex flex-col h-full">
                <motion.div
                  variants={{
                    hidden: { clipPath: "inset(100% 0% 0% 0%)" },
                    show: { clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 0.5, delay: revealBase + 0.27, ease: EASE } },
                  }}
                  className={`relative ${current.kenburns ? "h-[320px] md:h-[380px]" : "h-[250px] md:h-[290px]"} overflow-hidden bg-[#0B1726] transition-[height] duration-500`}
                >
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={`${active}-${current.image}`}
                      src={current.image}
                      alt={`${current.title.toLowerCase()} technical engineering visual`}
                      data-testid="design-panel-image"
                      className="absolute inset-0 w-full h-full object-cover"
                      initial={{ opacity: 0, x: 32, scale: 1.02 }}
                      animate={current.kenburns
                        ? current.pan
                          ? { opacity: 1, x: [0, -26, 0], scale: 1.1 }
                          : { opacity: 1, x: 0, scale: [1.02, 1.09, 1.02] }
                        : { opacity: 1, x: 0, scale: 1 }}
                      exit={{ opacity: 0, x: -24, scale: 1.01 }}
                      transition={current.kenburns
                        ? current.pan
                          ? {
                              opacity: { duration: 0.46, ease: EASE },
                              x: { duration: 20, repeat: Infinity, ease: "easeInOut" },
                              scale: { duration: 0.6, ease: EASE },
                            }
                          : {
                              opacity: { duration: 0.46, ease: EASE },
                              x: { duration: 0.46, ease: EASE },
                              scale: { duration: 18, repeat: Infinity, ease: "easeInOut" },
                            }
                        : { duration: 0.46, ease: EASE }}
                    />
                  </AnimatePresence>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1726]/70 via-[#0B1726]/20 to-transparent" />
                  <div className="absolute left-6 bottom-5 bg-[#101C2B]/90 border border-[rgba(255,255,255,0.12)] px-3 py-2 flex items-center gap-2 backdrop-blur-sm">
                    <ActiveIcon size={16} className="text-[#F47721]" />
                    <span className="font-manrope text-[10px] font-bold uppercase tracking-[0.2em] text-[#C8D2DC]">{current.badge}</span>
                  </div>
                </motion.div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={active}
                    initial={{ opacity: 0, x: 28 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.45, ease: EASE }}
                    className="p-7 md:p-9 flex flex-col flex-1"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-[2px] w-8 bg-[#F47721]" />
                      <span className="font-manrope text-[10px] font-bold uppercase tracking-[0.22em] text-[#9AB3CC]">Selected Capability</span>
                    </div>
                    <h3 className="font-manrope font-bold tracking-tight text-white text-[26px] md:text-[34px] leading-[1.12] mt-5" data-testid="design-panel-title">
                      {current.title}
                    </h3>
                    <p className="font-inter text-[#C8D2DC] text-[15px] md:text-base leading-[1.7] mt-4 max-w-2xl" data-testid="design-panel-description">
                      {current.desc}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-6" data-testid="design-panel-scope">
                      {current.scope.map((item) => (
                        <span key={item} className="font-inter text-[11px] font-semibold uppercase tracking-[0.1em] text-[#E8EDF2] bg-white/[0.04] border border-[rgba(255,255,255,0.12)] rounded px-3 py-2">
                          {item}
                        </span>
                      ))}
                    </div>
                    <button
                      type="button"
                      aria-expanded={scopeOpen}
                      data-testid="design-panel-full-scope-toggle"
                      onClick={() => setScopeOpen(!scopeOpen)}
                      className="mt-auto pt-8 inline-flex items-center gap-2 font-inter text-[12px] font-semibold uppercase tracking-[0.2em] text-[#F47721] hover:text-white transition-colors duration-300 w-fit"
                    >
                      {scopeOpen ? "Hide Full Scope" : "Full Scope"}
                      <ChevronDown size={16} className={`transition-transform duration-300 ${scopeOpen ? "rotate-180" : ""}`} />
                    </button>
                    <AnimatePresence initial={false}>
                      {scopeOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <div className="mt-7 border-t border-[rgba(255,255,255,0.12)] pt-6" data-testid="design-panel-full-scope">
                            <p className="font-inter text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F47721]">Full Scope</p>
                            <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
                              {current.details.map((item) => (
                                <li key={item} className="font-inter text-[13px] leading-relaxed text-[#E8EDF2] flex items-start gap-2.5">
                                  <span className="w-1.5 h-1.5 bg-[#F47721] mt-2 shrink-0" />
                                  {item}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </motion.div>
          </div>
        </div>

        <div className="mt-20 border-t border-[#E2E6E9] pt-14" data-testid="design-approach">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.5, ease: EASE }}
            className="font-inter text-[12px] font-semibold uppercase tracking-[0.3em] text-[#F47721]"
          >
            Our Design Approach
          </motion.p>
          <motion.h3
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
            className="font-manrope font-bold tracking-tight text-[#18232D] text-[34px] md:text-[42px] lg:text-[46px] leading-[1.1] mt-5"
            data-testid="design-approach-line"
          >
            CONCEPT. ENGINEER. COORDINATE. DELIVER.
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={VIEWPORT}
            transition={{ duration: 0.55, delay: 0.2, ease: EASE }}
            className="font-inter text-[#59636D] text-base md:text-[17px] leading-[1.65] mt-5 max-w-3xl"
            data-testid="design-approach-description"
          >
            Every design is developed with technical accuracy, interdisciplinary coordination and practical execution in mind — creating documentation that can move confidently from the design stage to site.
          </motion.p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="design-approach-stages">
            {APPROACH_STAGES.map((stage, i) => (
              <motion.div
                key={stage.num}
                data-testid={`design-approach-stage-${i + 1}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={VIEWPORT}
                transition={{ duration: 0.55, delay: 0.28 + i * 0.1, ease: EASE }}
                className="group bg-white border border-[#E5E8EA] rounded-md p-6 transition-[border-color,box-shadow,transform] duration-300 hover:-translate-y-1 hover:border-[#F47721]/45 hover:shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="font-manrope text-sm font-bold tracking-[0.18em] text-[#98A1AA] group-hover:text-[#F47721] transition-colors duration-300">{stage.num}</span>
                  <span className="h-[2px] w-8 bg-[#F47721]" />
                </div>
                <h4 className="font-manrope font-bold uppercase tracking-[0.1em] text-[#18232D] text-[15px] mt-7">{stage.title}</h4>
                <p className="font-inter text-[#59636D] text-[14px] leading-[1.65] mt-3">{stage.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={VIEWPORT}
          transition={{ duration: 0.65, ease: EASE }}
          className="relative mt-16 overflow-hidden rounded-md bg-[#172A3A] px-7 py-12 md:px-12 md:py-14"
          data-testid="design-final-cta"
        >
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.08] [background-image:linear-gradient(rgba(255,255,255,0.35)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.35)_1px,transparent_1px)] [background-size:56px_56px]" />
          <div aria-hidden="true" className="absolute right-0 top-0 h-full w-1/3 bg-gradient-to-l from-[#285A91]/25 to-transparent" />
          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
            <div className="max-w-2xl">
              <p className="font-inter text-[12px] font-semibold uppercase tracking-[0.3em] text-[#F47721]" data-testid="design-cta-label">
                Have A Project In Development?
              </p>
              <h3 className="font-manrope font-bold tracking-tight text-white text-[32px] md:text-[42px] leading-[1.12] mt-4" data-testid="design-cta-heading">
                Let's develop the engineering behind it.
              </h3>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <a
                href="#contact"
                onClick={go}
                data-testid="design-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#F47721] px-6 py-4 font-inter text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:bg-[#D96A1B]"
              >
                Discuss Your Design <ArrowUpRight size={16} />
              </a>
              <a
                href="#services-hero"
                onClick={(e) => go(e, "#services-hero")}
                data-testid="design-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-[rgba(255,255,255,0.25)] px-6 py-4 font-inter text-[12px] font-semibold uppercase tracking-[0.18em] text-white transition-colors duration-300 hover:border-[#F47721] hover:text-[#F47721]"
              >
                View Our Services <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
